import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

/**
 * Entrada de catalogo para un QR fisico (totem).
 *
 * Cada centro comercial de la marca tiene su propio archivo JSON
 * (por default: assets/config/qr-map.json), de modo que la misma base
 * de codigo Angular puede reutilizarse para distintos malls cambiando
 * solamente los assets estaticos y la seccion "informaciones".
 */
export interface QrEntry {
  /** Id numerico del totem en el backend (tot_id). */
  tot_id: number;
  /** Nombre humano del totem: "P1QR1", "P-2QR3", etc. */
  nombre: string;
  /** Piso asociado ("Piso 1", "Piso -2", ...). */
  piso: string;
  /** Codigo cifrado principal (hash que viaja en la URL). */
  codigo: string;
  /**
   * Codigos alternos conocidos para el mismo totem. Si un QR se cambio
   * o rota el hash, aqui se listan los "legacy" para que sigan mapeando
   * al mismo totem.
   */
  aliases?: string[];
}

export interface QrCatalogDoc {
  /** Identificador del mall (informativo, util para logs/analytics). */
  mall?: string;
  /** Archivo fuente del que se genero el JSON (informativo). */
  generatedFrom?: string;
  /** Version de schema, por si cambia la forma en el futuro. */
  schema?: number;
  /** Catalogo propiamente tal. */
  qrs: QrEntry[];
}

/**
 * Servicio que carga y resuelve el catalogo de QRs.
 *
 * Uso tipico:
 *   constructor(private qrCatalog: QrCatalogService) {}
 *
 *   await this.qrCatalog.ensureLoaded();
 *   const match = this.qrCatalog.findByCodigo(codigo);
 */
@Injectable({ providedIn: 'root' })
export class QrCatalogService {
  private doc: QrCatalogDoc = { qrs: [] };
  /** Indice codigo -> entrada, incluyendo aliases, para lookup O(1). */
  private byCodigo: Map<string, QrEntry> = new Map();
  private loadPromise: Promise<QrCatalogDoc> | null = null;
  private loaded = false;

  constructor(private http: HttpClient) {}

  /**
   * Dispara la carga del catalogo si no se hizo todavia.
   * Se puede llamar muchas veces: solo se hace una peticion.
   */
  ensureLoaded(): Promise<QrCatalogDoc> {
    if (this.loaded) {
      console.log('[QrCatalog] Catalogo ya cargado');
      return Promise.resolve(this.doc);
    }
    if (!this.loadPromise) {
      const url = (environment as any).qrMapUrl || 'assets/config/qr-map.json';
      console.log('[QrCatalog] Cargando desde:', url);
      this.loadPromise = this.http
        .get<QrCatalogDoc>(url)
        .toPromise()
        .then((doc) => {
          this.doc = doc && doc.qrs ? doc : { qrs: [] };
          console.log('[QrCatalog] Cargado. Totales:', (this.doc.qrs || []).length);
          this.indexar();
          this.loaded = true;
          return this.doc;
        })
        .catch((err) => {
          console.error('[QrCatalog] Error al cargar:', err);
          this.doc = { qrs: [] };
          this.indexar();
          this.loaded = true;
          return this.doc;
        });
    }
    return this.loadPromise;
  }

  /** Devuelve el catalogo entero (o vacio si aun no cargo). */
  getAll(): QrEntry[] {
    return this.doc.qrs || [];
  }

  /**
   * Busca una entrada por codigo exacto o por alias.
   * Devuelve null si no hay match.
   */
  findByCodigo(codigo: string): QrEntry | null {
    if (!codigo) return null;
    return this.byCodigo.get(codigo) || null;
  }

  /**
   * Indexa el documento actual por codigo y aliases, para lookups rapidos.
   * Se invoca automaticamente tras cargar.
   *
   * IMPORTANTE: los codigos viajan en la URL y pueden llegar en forma
   * codificada (%2F, %2B, %3D) o ya decodificada (/, +, =), dependiendo
   * de como Angular resuelva el paramMap. Para evitar "QR desconocido"
   * falsos, indexamos cada codigo tanto en su forma original como en
   * su variante decodificada y codificada.
   */
  private indexar() {
    this.byCodigo = new Map();
    for (const entry of this.doc.qrs || []) {
      if (entry && entry.codigo) {
        this.registrarClave(entry.codigo, entry);
      }
      if (entry && entry.aliases && entry.aliases.length) {
        for (const alias of entry.aliases) {
          if (alias) this.registrarClave(alias, entry);
        }
      }
    }
  }

  /**
   * Registra la clave en el indice junto con sus variantes decodificada
   * y codificada (si difieren de la original), para que findByCodigo
   * acierte sea cual sea la forma en que Angular entregue el parametro.
   */
  private registrarClave(clave: string, entry: QrEntry) {
    if (!clave) return;
    this.byCodigo.set(clave, entry);
    try {
      const decoded = decodeURIComponent(clave);
      if (decoded && decoded !== clave) {
        this.byCodigo.set(decoded, entry);
      }
    } catch (_) { /* URL mal formada, ignorar */ }
    try {
      const encoded = encodeURIComponent(clave);
      if (encoded && encoded !== clave) {
        this.byCodigo.set(encoded, entry);
      }
    } catch (_) { /* ignorar */ }
  }
}
